'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useUserResourceSource } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import {
  AnonymousFallback,
  ButtonSkeleton,
  IslandKeyframes,
  ToggleErrorCaption,
  toggleActive,
  toggleError,
  toggleInactive,
} from './_shared';
import { useToggleErrorMessage } from './useToggleErrorMessage';

export interface WatchLaterButtonProps {
  videoSlug: string;
  signInHref?: string;
}

export function WatchLaterButton({ videoSlug, signInHref }: WatchLaterButtonProps) {
  const user = useCurrentUser<{ name?: string }>();
  const ur = useUserResourceSource();
  const [toggling, setToggling] = useState(false);
  const [optimistic, setOptimistic] = useState<boolean | null>(null);
  const { t } = useTranslation('site-builder-blocks');
  const { error, setError, clearError } = useToggleErrorMessage();

  const state = useAsyncResource<boolean>(
    async (signal) => {
      if (!user || !ur?.watchLater) return null;
      return (await ur.watchLater.has(videoSlug, signal)) ?? false;
    },
    [user, ur, videoSlug],
  );

  if (!user)
    return (
      <>
        <IslandKeyframes />
        <AnonymousFallback
          variant="button"
          prompt={t('islands.signInToSave')}
          {...(signInHref ? { signInHref } : {})}
        />
      </>
    );
  if (!ur?.watchLater) return null;
  if (state.kind === 'loading')
    return (
      <>
        <IslandKeyframes />
        <ButtonSkeleton width="140px" />
      </>
    );

  const saved = optimistic ?? (state.kind === 'loaded' ? state.data : false);
  const handle = async () => {
    if (toggling) return;
    clearError();
    setToggling(true);
    setOptimistic(!saved);
    try {
      const res = await ur.watchLater!.toggle({ videoSlug });
      if (!res || res.skipped) setOptimistic(null);
      else setOptimistic(Boolean(res.active));
    } catch (err) {
      // Roll back optimistic state and surface the failure inline.
      setOptimistic(null);

      console.error('[WatchLaterButton] toggle failed', err);
      setError(t('islands.toggleFailed'));
    } finally {
      setToggling(false);
    }
  };

  if (state.kind === 'error')
    return (
      <button type="button" onClick={() => location.reload()} style={toggleError}>
        {t('islands.save.retry')}
      </button>
    );

  return (
    <>
      <button
        type="button"
        onClick={handle}
        disabled={toggling}
        aria-pressed={saved}
        style={saved ? toggleActive : toggleInactive}
      >
        {saved ? t('islands.watchLater.saved') : t('islands.watchLater.save')}
      </button>
      {error && <ToggleErrorCaption message={error} />}
    </>
  );
}
